
import {defineComponent, PropType} from "vue";
import {useToast} from "vue-toastification";
import {ErrorHandlerQueue} from "@/error/ErrorHandlerQueue";
import {DpExceptionsErrorHandler} from "@/error/handlers/DpExceptionsErrorHandler";
import {UIStateDto} from "@/dtos/UIStateDto";
import {AxiosErrorHandler} from "@/error/handlers/AxiosErrorHandler";
import AlertError2 from "@/components/UI/Bootstrap/Alert/AlertError2.vue";
import BaseTitle from "@/components/UI/BaseTitle.vue";
import BaseContainer from "@/components/UI/Bootstrap/BaseContainer.vue";
import {MasterProductCategoryRestDto} from "@/api/pharma-cpc-product/models";
import {ProductRestService} from "@/services/rest/cpc-product/ProductRestService";
import {
  ProductCategoryCustomerNameListRestDto,
  ProductCategoryCustomerNameOverviewRestDto,
  ProductCategoryCustomerNameOverviewResultsRestDto
} from "@/api/pharma-cpc-product-category-mgmt/models";
import {ProductCategoryRestService} from "@/services/rest/cpc-product-category-mgmt/ProductCategoryRestService";
import {MultilingualFieldRestDto} from "@/api/pharma-cpc-mgmt/models";
import {
  MultilingualFieldRestDtoModel
} from "@/models/api/pharma-cpc-product-category-mgmt/MultilingualFieldRestDtoModel";
import AutoSaveFeedback from "@/components/UI/AutoSaveFeedback.vue";
import {AutoSaveFeedbackStatusEnum} from "@/models/enum/AutoSaveFeedbackStatusEnum";
import RenameDPSubCategory from "@/components/layouts/catalog/categories/RenameDPSubCategory.vue";
import {AbilityContext} from "@/context/AbilityContext";

export default defineComponent({
  name: "RenameDPSubCategories",
  components: {
    RenameDPSubCategory,
    AutoSaveFeedback,
    BaseContainer,
    BaseTitle,
    AlertError2,
  },
  props: {
    language: {
      type: String,
      required: true
    },
    allLanguages: {
      type: Array as PropType<string[]>,
      required: true
    },
  },
  data() {
    return {
      toaster: useToast(),
      pageUI: UIStateDto.createWithDefaults(),
      autoSaveFeedbackStatus: AutoSaveFeedbackStatusEnum.NONE,
      subCategoryNames: null as ProductCategoryCustomerNameOverviewResultsRestDto | null,
      categoryTree: null as MasterProductCategoryRestDto | null,
      customNamesMap: {} as Record<string, ProductCategoryCustomerNameOverviewRestDto>,
      open: new Set() as Set<string>,
    }
  },
  mounted() {
    this.reloadContent();
  },
  watch: {
    language: {
      handler() {
        this.reloadContent();
      },
      immediate: false
    }
  },
  computed: {
    hasChanges(): boolean {
      return this.autoSaveFeedbackStatus === AutoSaveFeedbackStatusEnum.UNSAVED_CHANGES;
    },
    AutoSaveFeedbackStatusEnum() {
      return AutoSaveFeedbackStatusEnum;
    },
    dynamicHeight() {
      const baseHeight = AbilityContext.isAuthorizedForFeature('MULTILINGUAL') ? 350 : 300;
      return `calc(100vh - ${baseHeight}px)`;
    }
  },
  methods: {
    async reloadContent() {
      this.pageUI.clearError().setNotReady();
      try {
        // Load existing custom names
        this.subCategoryNames = await ProductCategoryRestService.getInstance().findProductCategoryCustomerNames();
        this.categoryTree = await ProductRestService.getInstance().getProductCategoriesMasterTree();

        // Initialize custom names map
        this.initializeCustomNamesMap();
        this.autoSaveFeedbackStatus = AutoSaveFeedbackStatusEnum.NONE;
      } catch (exceptions: any) {
        this.setExceptions(exceptions);
        window.scrollTo(0, 0);
      } finally {
        this.pageUI.setReady();
      }
    },

    initializeCustomNamesMap() {
      this.customNamesMap = {};

      const traverse = (category: MasterProductCategoryRestDto) => {
        const code = category.code;

        let customName: ProductCategoryCustomerNameOverviewRestDto | undefined =
            this.subCategoryNames?.results?.find(item => item.code === code);
        if (!customName) {
          customName = {
            code,
            custom_name: MultilingualFieldRestDtoModel.createWithDefaults()
          };
        }

        this.customNamesMap[code] = customName;
        category.subcategories?.forEach((subCategory: MasterProductCategoryRestDto) => traverse(subCategory));
      };

      traverse(this.categoryTree!);
    },

    toggleOpen(code: string) {
      if (this.open.has(code)) {
        this.open.delete(code);
      } else {
        this.open.add(code);
      }
    },

    getCustomItemName(category: MasterProductCategoryRestDto): string {

      const originalName: string = (category.name as MultilingualFieldRestDto)[this.language as keyof MultilingualFieldRestDto]!;
      const customName: ProductCategoryCustomerNameOverviewRestDto | undefined = this.customNamesMap[(category.code)];
      if (customName) {
        return customName.custom_name?.[this.language as keyof MultilingualFieldRestDto] ?? originalName;
      }
      return originalName;
    },
    isOpen(code: string): boolean {
      return this.open.has(code);
    },

    getNamesToSave(): ProductCategoryCustomerNameOverviewRestDto[] {
      const nonEmpty: ProductCategoryCustomerNameOverviewRestDto[] = [];

      for (const name of Object.values(this.customNamesMap)) { // Use Object.values instead of Map.values
        // Check if there are any non-empty values
        const hasNonEmptyValues = this.allLanguages.some(lang =>
            (name.custom_name?.[lang as keyof MultilingualFieldRestDto] || '').trim() !== ''
        );
        if (hasNonEmptyValues) {
          nonEmpty.push(name);
        }
      }

      return nonEmpty;
    },
    onNameChanged(): void {
      this.autoSaveFeedbackStatus = AutoSaveFeedbackStatusEnum.UNSAVED_CHANGES;
    },

    async submitData() {
      this.pageUI
          .clearError()
          .setNotReady();
      this.autoSaveFeedbackStatus = AutoSaveFeedbackStatusEnum.BUSY;

      try {
        const namesToSave: ProductCategoryCustomerNameListRestDto = {
          category_names: this.getNamesToSave()
        };
        await ProductCategoryRestService.getInstance().updateProductCategoryCustomerNames(namesToSave);

        this.toaster.success(this.$t('savedSuccessfully'));
        await this.reloadContent();
        this.autoSaveFeedbackStatus = AutoSaveFeedbackStatusEnum.NONE;
      } catch (exceptions: any) {
        this.setExceptions(exceptions);
        window.scrollTo(0, 0);
      } finally {
        this.pageUI.setReady();
      }
    },

    setExceptions(exceptions: unknown): void {
      ErrorHandlerQueue
          .create()
          .add(DpExceptionsErrorHandler.createWithDefaultUIStateBehavior(this.pageUI as UIStateDto))
          .add(AxiosErrorHandler.createWithDefaultUIStateBehavior(this.pageUI as UIStateDto))
          .catch(exceptions, true);
    },
  },
});
